import { AttachFile, Delete } from "@mui/icons-material";
import { Alert, Box, Button, Divider, FormControlLabel, Grid, IconButton, InputLabel, Radio, RadioGroup, Typography } from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Slider from "react-slick";
import ClaimApiService from "../../adapters/xhr/ClaimApiService";
import TrainStationApiService from "../../adapters/xhr/TrainStationApiService";
import { MyButton } from "../../components/my-button/MyButton";
import { MyCheckbox } from "../../components/my-checkbox/MyCheckbox";
import { getConfirmation } from "../../components/my-confirmation-dialog/MyConfirmationDialog";
import { MyDatePicker } from "../../components/my-date-picker/MyDatePicker";
import { MyDropdown } from "../../components/my-dropdown/MyDropdown";
import { MyTextField } from "../../components/my-text-field/MyTextField";
import { MyTimePicker } from "../../components/my-time-picker/MyTimePicker";
import { AllowedFileExtensions, DepartureTimeExp, EmailRegExp, lngs, MaxReceiptSize, PhoneNumberRegExp, SliderSettings, ValidTextRegExp } from "../../Constant";
import MyContext from "../../contexts/MyContext";
import { TrainTheme } from "../../infrastructure/TrainThemeProvider";
import Logosvg from "../../static/malartag_favicon_blue.svg";
import Logopng from "../../static/malartag_favicon_blue_png.png";
import { formatDate, formatDepartureDate, formatTime, getTrainStationOptions, toBase64 } from "../../Utils";

export const TrainContactPage = (props) => {
  const MaxNumberOfAttachments = 5;
  const { t, i18n } = useTranslation();
  let sliderRef = React.createRef();
  const myContext = useContext(MyContext);
  const [caseNumber, setCaseNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [surName, setSurName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [comment, setComment] = useState("");
  const [departureStation, setDepartureStation] = useState(null);
  const [arrivalStation, setArrivalStation] = useState(null);
  const [departureDate, setDepartureDate] = useState(null);
  const [departureTime, setDepartureTime] = useState(null);
  const [agreePersonalDataProcessing, setAgreePersonalDataProcessing] = useState(false);
  const [questionRelatedToTrip, setQuestionRelatedToTrip] = useState(false);
  const [requestResponse, setRequestResponse] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [timeOptions, setTimeOptions] = useState(null);

  const [error, setError] = useState(null);
  const [surNameErr, setSurNameErr] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [firstNameErr, setFirstNameErr] = useState("");
  const [phoneNumberErr, setPhoneNumberErr] = useState("");
  const [commentErr, setCommentErr] = useState("");
  const [departureStationValueErr, setDepartureStationValueErr] = useState("");
  const [departureDateValueErr, setDepartureDateValueErr] = useState("");
  const [departureTimeValueErr, setDepartureTimeValueErr] = useState(null);
  const [arrivalStationValueErr, setArrivalStationValueErr] = useState("");
  const [agreePersonalDataProcessingErr, setAgreePersonalDataProcessingErr] = useState("");

  const hiddenFileInput = React.useRef(null);

  const handleClick = (event) => {
    hiddenFileInput.current.click();
  };

  const removeAttachment = (fileName) => {
    setAttachments(attachments.filter((item) => item.name !== fileName));
  };

  const handleChangeAttachment = (event) => {
    const fileUploaded = event.target.files[0];
    if (myContext.allowFileUploadExtension.find((extension) => fileUploaded.name.toLowerCase().endsWith(extension.toLowerCase())) == null) setError("not_allowed");
    else if (fileUploaded.size > MaxReceiptSize) setError("your_uploaded_file_too_big");
    else {
      setAttachments((oldArray) => [...oldArray.filter((item) => item.name !== fileUploaded.name), fileUploaded]);
      setError(null);
    }
  };

  useEffect(() => {
    document.title = "Mälardalstrafik - Synpunkter och frågor";
    // append link tag to document head
    document.head.appendChild(Object.assign(document.createElement("link"), { rel: "icon", type: "image/png", href: Logopng }));
    document.head.appendChild(Object.assign(document.createElement("link"), { rel: "icon", type: "image/svg+xml", href: Logosvg }));
  }, []);

  const validateEmail = () => {
    let validEmail = new RegExp(EmailRegExp);
    if (requestResponse && !email.trim()) {
      setEmailErr("required");
      return false;
    } else if (!requestResponse && !email.trim()) {
      setEmailErr("");
      return true;
    } else if (!validEmail.test(email.trim())) {
      setEmailErr("invalid");
      return false;
    } else setEmailErr("");
    return true;
  };

  const validatePhone = () => {
    let validPhoneNumber = new RegExp(PhoneNumberRegExp);
    if (phoneNumber.trim()) {
      if (!validPhoneNumber.test(phoneNumber.trim())) {
        setPhoneNumberErr("invalid");
        return false;
      } else {
        setPhoneNumberErr("");
        return true;
      }
    } else {
      setPhoneNumberErr("");
      return true;
    }
  };

  const fetchTrainStations = useCallback(async () => {
    let response = await TrainStationApiService.getAll();
    if (response.isSuccess) {
      myContext.changeTrainStations(response.data);
    }
  }, []);

  const fetchDepartureTimeList = async () => {
    let response = await TrainStationApiService.getDepartureTimeList(departureStation.code, arrivalStation.code, null, formatDate(departureDate));
    if (response.isSuccess) {
      let data = response.data
        .map((option, key) => {
          let rObj = {};
          rObj["key"] = key;
          rObj["value"] = formatTime(option);
          rObj["data"] = option;
          return rObj;
        })
        .sort((a, b) => {
          if (new Date(a.data) > new Date(b.data)) {
            return 1;
          }
          if (new Date(a.data) < new Date(b.data)) {
            return -1;
          }
          return 0;
        });
      setTimeOptions(data);
    }
  };

  const handleChangeDepartureTime = (value) => {
    let departureTimeReg = new RegExp(DepartureTimeExp);
    if (departureTimeReg.test(value)) {
      setDepartureTime(value);
    } else setDepartureTime(null);
  };

  const getAllowedFileExtension = useCallback(async () => {
    let response = await ClaimApiService.getAllowedFileExtension();
    if (response.isSuccess && response.data) myContext.changeAllowFileUploadExtension(response.data);
    else myContext.changeAllowFileUploadExtension(AllowedFileExtensions.map((item) => item.key));
  }, []);

  useEffect(() => {
    sliderRef.slickGoTo(myContext.activeSlide, false);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [myContext.activeSlide]);

  useEffect(() => {
    if (!myContext.trainStations || myContext.trainStations.length === 0) fetchTrainStations();
    if (!myContext.allowFileUploadExtension || myContext.allowFileUploadExtension.length === 0) getAllowedFileExtension();
  }, []);

  useEffect(() => {
    if (departureDate && departureStation && arrivalStation && !departureTime) {
      fetchDepartureTimeList();
      setDepartureTime(null);
    }
  }, [departureDate, departureStation, arrivalStation]);

  const validate = async () => {
    let isValid = true;

    if (!comment.trim()) {
      setCommentErr("required");
      isValid = false;
    } else {
      setCommentErr("");
    }

    if (requestResponse && !firstName.trim()) {
      setFirstNameErr("required");
      isValid = false;
    } else {
      setFirstNameErr("");
    }

    if (requestResponse && !surName.trim()) {
      setSurNameErr("required");
      isValid = false;
    } else {
      setSurNameErr("");
    }

    if (!validateEmail()) isValid = false;

    if (!validatePhone()) isValid = false;

    if (questionRelatedToTrip && !departureStation) {
      setDepartureStationValueErr("required");
      isValid = false;
    } else {
      setDepartureStationValueErr("");
    }

    if (questionRelatedToTrip && !arrivalStation) {
      setArrivalStationValueErr("required");
      isValid = false;
    } else {
      setArrivalStationValueErr("");
    }

    if (questionRelatedToTrip && !departureDate) {
      setDepartureDateValueErr("required");
      isValid = false;
    } else {
      setDepartureDateValueErr("");
    }

    if (questionRelatedToTrip && !departureTime) {
      setDepartureTimeValueErr("required");
      isValid = false;
    } else {
      setDepartureTimeValueErr("");
    }

    if (isValid && requestResponse && !agreePersonalDataProcessing) {
      setAgreePersonalDataProcessingErr("agree_personal_data_processing_warning");
      isValid = false;
    } else {
      setAgreePersonalDataProcessingErr("");
    }

    if (isValid) {
      let result = await ClaimApiService.createContact({
        requestResponse: requestResponse,
        fullName: `${firstName} ${surName}`,
        email: email?.trim(),
        phoneNumber: phoneNumber?.trim(),
        comment: comment?.trim(),
        departureStation: departureStation?.value,
        arrivalStation: arrivalStation?.value,
        departureDate: !!departureDate && !!departureTime ? `${formatDepartureDate(departureDate, departureTime).toISOString()}` : null,
        attachments: await Promise.all(
          attachments.map(async (file) => {
            return {
              file: await toBase64(file),
              fileName: file.name,
            };
          })
        ),
      });
      if (!result?.isSuccess) {
        const confirmed = await getConfirmation({
          title: t("error"),
          message: t("refresh_page_message"),
          theme: TrainTheme,
        });
        if (confirmed) window.location.reload();
      } else {
        setFirstName("");
        setSurName("");
        setPhoneNumber("");
        setEmail("");
        setComment("");
        setAttachments([]);
        setDepartureStation(null);
        setArrivalStation(null);
        setDepartureDate(null);
        setDepartureTime(null);
        setQuestionRelatedToTrip(false);
        setAgreePersonalDataProcessing(false);
        setTimeOptions(null);
        setError(null);
        setCaseNumber(result.data);
        myContext.changeActiveSlide((myContext.activeSlide += 1));
      }
    }
  };

  const submitAnotherClaim = () => {
    setRequestResponse(false);
    myContext.changeActiveSlide(0);
  };

  return (
    <MyContext.Consumer>
      {(context) => (
        <Grid container className="contact page container custom padding top-2 bottom-2 left-0 right-0" justifyContent="center">
          <Grid item sx={{ label: { fontWeight: "bold", color: "#222" } }}>
            <Slider ref={(slider) => (sliderRef = slider)} {...SliderSettings} beforeChange={(_, next) => context.changeActiveSlide(next)}>
              <div hidden={context.activeSlide !== 0} className="padding left-1 right-1">
                <Box component="small" sx={{ a: { color: "primary.main" } }} dangerouslySetInnerHTML={{ __html: t("contact_description") }} />
                <InputLabel style={{ marginTop: "1.25rem" }} className="margin bottom-0" htmlFor="comment">{`${t("contact_comment")}*`}</InputLabel>
                <Typography component="em" style={{ width: "100%", marginBottom: "0.5rem" }} dangerouslySetInnerHTML={{ __html: t("contact_comment_desc") }} />
                <MyTextField id="comment" placeholder={t("contact_comment")} required={true} helperText={t(commentErr)} regExp={ValidTextRegExp} value={comment} onChange={setComment} multiline={true} rows={4} style={{ marginBottom: "0", marginTop: "0.5rem" }} />
                <MyCheckbox label={t("question_related_to_trip")} checked={questionRelatedToTrip} onChange={setQuestionRelatedToTrip} className="margin top-1" />
                {questionRelatedToTrip && (
                  <>
                    <InputLabel htmlFor="departureStation">{`${t("departure_station")}*`}</InputLabel>
                    <MyDropdown noOptionsText={t("no_options")} id="departureStation" required={true} helperText={t(departureStationValueErr)} onChange={(e, value) => setDepartureStation(value)} placeholder={t("departure_station")} value={departureStation} options={getTrainStationOptions(myContext.trainStations).filter((option) => option.key !== arrivalStation?.key)} className="margin top-0" />
                    <InputLabel htmlFor="arrivalStation">{`${t("arrival_station")}*`}</InputLabel>
                    <MyDropdown noOptionsText={t("no_options")} id="arrivalStation" required={true} helperText={t(arrivalStationValueErr)} onChange={(e, value) => setArrivalStation(value)} placeholder={t("arrival_station")} value={arrivalStation} options={getTrainStationOptions(myContext.trainStations).filter((option) => option.key !== departureStation?.key)} className="margin top-0" />
                    <InputLabel htmlFor="departureDate">{`${t("departure_date")}*`}</InputLabel>
                    <MyDatePicker id="departureDate" required={true} error={departureDateValueErr !== ""} value={departureDate} onAccept={(date) => setDepartureDate(date?.toString())} helperText={t(departureDateValueErr)} maxDate={new Date()} placeholder={t("date_format")} cancelLabel={t("cancel")} okLabel={t("ok")} locale={i18n.language} views={["day"]} className="margin top-0" />
                    <InputLabel htmlFor="departureTime">{`${t("departure_time")}*`}</InputLabel>
                    {timeOptions == null || timeOptions.length == 0 ? <MyTimePicker id="departureTime" required={true} className="margin bottom-2 top-0" onAccept={(date) => setDepartureTime(date?.toString())} value={departureTime} placeholder={t("departure_time")} helperText={t(departureTimeValueErr)} cancelLabel={t("cancel")} okLabel={t("ok")} /> : <MyDropdown id="departureTime" required={true} className="margin bottom-2 top-0" helperText={t(departureTimeValueErr)} onChange={(e, time) => handleChangeDepartureTime(time !== null ? time.value : null)} freeSolo={true} placeholder={`HH:mm`} onFreeSolo={(e) => handleChangeDepartureTime(e.target.value)} value={departureTime} options={timeOptions} />}
                  </>
                )}
                <Box component="h2" color="primary.contactHeading" className="margin bottom-0" sx={{ fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2.2rem", lg: "2.4rem", xl: "2.25rem" } }}>
                  {t("contact_attachments")}
                </Box>
                <p dangerouslySetInnerHTML={{ __html: t("contact_attachments_note", { files: context.allowFileUploadExtension.map((extension) => `.${extension}`.toLowerCase()).join(", ") }) }} />
                {error && (
                  <Alert variant="filled" severity="error" className="margin top-1 bottom-1">
                    {t(error)}
                  </Alert>
                )}
                {(!attachments || attachments.length > 0) && (
                  <>
                    <hr className="margin top-0 bottom-0" aria-hidden={true} />
                    {attachments.map((item, key) => (
                      <Grid container key={key} justifyContent="space-between" alignItems={"center"}>
                        <Grid xs={10} sm={10} item color="primary.main" container wrap="nowrap" alignItems={"center"} style={{ overflowX: "hidden" }}>
                          <AttachFile fontSize="small" color="primary.main" style={{ marginRight: "1rem", marginLeft: "0.5rem" }} />
                          <Box whiteSpace={"nowrap"} component="small" color="primary.main">
                            {item.name}
                          </Box>
                        </Grid>
                        <Grid item>
                          <IconButton aria-label={t("delete")} onClick={(e) => removeAttachment(item.name)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                  </>
                )}
                {(!attachments || attachments.length < MaxNumberOfAttachments) && (
                  <>
                    <hr className="margin top-0 bottom-0" aria-hidden={true} />
                    <Button
                      aria-haspopup="dialog"
                      variant="text"
                      className="button secondary w-100 justify-content-start"
                      style={{
                        textTransform: "unset",
                      }}
                      onClick={handleClick}
                    >
                      <AttachFile fontSize="small" color="primary.main" style={{ marginRight: "1rem" }} />
                      <span>{t("add_receipt_or_certificate")}</span>
                    </Button>
                    <input
                      accept={context.allowFileUploadExtension.map((extension) => `.${extension}`.toLowerCase()).join()}
                      type="file"
                      ref={hiddenFileInput}
                      onChange={handleChangeAttachment}
                      onClick={(event) => {
                        event.target.value = null;
                      }}
                      style={{ display: "none" }}
                    />
                  </>
                )}
                <Divider className="margin top-0" />
                <Box component="h2" color="primary.contactHeading" sx={{ fontSize: { xs: "1.4rem", sm: "1.8rem", md: "2.2rem", lg: "2.4rem", xl: "2.25rem" } }} className="margin top-2 bottom-1">
                  {t("contact_request_response_from_malardalstrafik")}
                </Box>
                <RadioGroup value={requestResponse} onChange={(e) => setRequestResponse(e.target.value === "true")} className="margin bottom-1">
                  <FormControlLabel value={false} control={<Radio className="padding top-0 bottom-0" />} label={t("contact_request_response_from_malardalstrafik_no")} />
                  <FormControlLabel className="margin bottom-0" value={true} control={<Radio className="padding top-0 bottom-0" />} label={t("contact_request_response_from_malardalstrafik_yes")} />
                </RadioGroup>
                {requestResponse && (
                  <>
                    <InputLabel htmlFor="firstName">{`${t("firstname")}*`}</InputLabel>
                    <MyTextField id="firstName" maxLength={100} required={requestResponse} value={firstName} placeholder={t("firstname")} helperText={t(firstNameErr)} onChange={setFirstName} regExp={ValidTextRegExp} className="margin top-0" />
                    <InputLabel htmlFor="surName">{`${t("surname")}*`}</InputLabel>
                    <MyTextField id="surName" value={surName} required={requestResponse} regExp={ValidTextRegExp} placeholder={t("surname")} maxLength={100} helperText={t(surNameErr)} onChange={setSurName} className="margin top-0" />
                    <InputLabel htmlFor="phoneNumber">{`${t("mobile_number")}`}</InputLabel>
                    <MyTextField id="phoneNumber" maxLength={15} value={phoneNumber} regExp={ValidTextRegExp} placeholder={t("mobile_number")} helperText={t(phoneNumberErr)} onChange={setPhoneNumber} className="margin top-0" />
                    <InputLabel htmlFor="email">{`${t("email")}*`}</InputLabel>
                    <MyTextField id="email" maxLength={100} style={{ marginBottom: "0" }} value={email} regExp={ValidTextRegExp} placeholder={t("email")} required={requestResponse} helperText={t(emailErr)} onChange={setEmail} className="margin top-0" />
                    <MyCheckbox helperText={t(agreePersonalDataProcessingErr)} label={t("contact_declaration")} checked={agreePersonalDataProcessing} onChange={setAgreePersonalDataProcessing} className="margin top-1" />
                    <Typography sx={{ flex: "1 1 100%", a: { color: "primary.main" } }} component="p" dangerouslySetInnerHTML={{ __html: t("contact_read_about_how_data_processed") }} />
                  </>
                )}
                <MyButton onClick={validate} text={t("submit")} style={{ float: "right" }} />
              </div>
              <div hidden={context.activeSlide !== 1}>
                <Grid container spacing={3} style={{ paddingLeft: "1rem", paddingRight: "1rem" }}>
                  <Grid item>
                    {requestResponse && <div dangerouslySetInnerHTML={{ __html: t("contact_thank_you_for_your_complaint_request_response") }} />}
                    {!requestResponse && <div dangerouslySetInnerHTML={{ __html: t("contact_thank_you_for_your_complaint") }} />}
                  </Grid>
                </Grid>
              </div>
            </Slider>
          </Grid>
        </Grid>
      )}
    </MyContext.Consumer>
  );
};
